import React, { useEffect, useState, memo, useCallback } from 'react';
import set from 'lodash/set';
import get from 'lodash/get';
import styles from './FeedbackForm.module.scss';
import {
  FeedbackForm as FeedbackFormType,
  FeedbackFormElement, FeedbackFormElementMultiScale,
  FeedbackFormElementSelect,
  FeedbackFormElementText,
  FeedbackFormElementTypeEnum,
} from 'src/types';
import { TextInput, TextInputValue } from 'src/components/text-input/TextInput';
import RadioGroup from 'src/components/radio-group/RadioGroup';
import CheckboxGroup from 'src/components/checkbox-group/CheckboxGroup';
import Button from 'src/components/button/Button';
import { feedbackFormHasAnswerErrors, feedbackSingleFormHasAnswerError } from 'src/utils/validation';
import { useMediaQuery } from 'react-responsive';
import MultiScaleComponent from '@src/pages/feedback-form/feedback-form/multi-scale/MultiScaleElement';
import { logger } from '../../../../utils/logger';

type Props = {
  form: FeedbackFormType;
  onSubmit: (form: string) => void;
  loading: boolean;
};

const fillInErrorMessage = 'Please fill in the field';

function FeedbackForm(props: Props) {
  const { form, onSubmit, loading } = props;

  const [elements, setElements] = useState<FeedbackFormElement[]>([]);
  const [showError, setShowError] = useState(false);
  const [currentIndex, setIndex] = useState(0);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    if (form?.elements) {
      setElements(form.elements);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.elements?.length]);

  const handleChangeExplanation = useCallback((index: number) => (value: TextInputValue) => {
    set(elements, `${index}.answer.answerExplanation`, value);
    setElements([...elements]);
    if (showError) setShowError(false);
  }, [elements, showError]);

  const handleChangeText = useCallback((index: number) => (value: TextInputValue) => {
    set(elements, `${index}.answer.value`, value);
    setElements([...elements]);
    if (showError) setShowError(false);
  }, [elements, showError]);

  const handChangeRadio = useCallback((index: number) => (value: string) => {
    set(elements, `${index}.answer.value`, value);

    setElements([...elements]);
    if (showError) setShowError(false);
  }, [elements, showError]);

  const handleChangeCheckbox = useCallback((index: number) => (value: string[]) => {
    set(elements, `${index}.answer.value`, value);

    setElements([...elements]);
    if (showError) setShowError(false);
  }, [elements, showError]);

  const handleChangeMultiScaleAnswer = useCallback((index: number) => (id: string, value: string) => {
    set(elements, `${index}.answer.${id}`, value);

    setElements([...elements]);
    if (showError) setShowError(false);
  }, [elements, showError]);

  const handleSubmit = useCallback(() => {
    // Check if the current question has validation errors (unanswered questions)
    const currentQuestionHasError = currentIndex < 0 ? false : feedbackSingleFormHasAnswerError(elements[currentIndex]);
    
    if (currentQuestionHasError) {
      // If current question isn't answered, show error and don't proceed
      setShowError(true);
      logger.debug(`[Exit Ticket] Current question ${currentIndex+1} is incomplete`);
      return;
    }
    
    // Check if any questions in the form have validation errors
    let hasErrors = false;
    for (let i = 0; i < elements.length; i++) {
      if (feedbackSingleFormHasAnswerError(elements[i])) {
        // If we find an unanswered question, navigate to it
        setIndex(i);
        setShowError(true);
        logger.debug(`[Exit Ticket] Question ${i+1} is incomplete, navigating to it`);
        hasErrors = true;
        break;
      }
    }
    
    // Only submit if all questions are answered
    if (!hasErrors) {
      logger.debug(`[Exit Ticket] All questions complete, submitting form`);
      const data = {
        ...form,
        elements
      };
      onSubmit(JSON.stringify(data));
    }
  }, [currentIndex, elements, form, onSubmit]);

  const renderQAItem = useCallback((element: FeedbackFormElement, index: number) => {
    let content;
    switch (element.type) {
      case FeedbackFormElementTypeEnum.text: {
        const e = element as FeedbackFormElementText;
        const hasError = showError && !e.answer?.value;
        content = (
          <TextInput
            textArea
            label={e.value}
            // placeholder={e.value}
            id={e.id}
            value={e.answer?.value ?? ''}
            onChange={handleChangeText(index)}
            hasError={hasError}
            subTitle={hasError ? fillInErrorMessage : ''}
            showValidationMessage
            allowSpecialCharacters={false}
          />
        );
        break;
      }
      case FeedbackFormElementTypeEnum.textarea: {
        const e = element as FeedbackFormElementText;
        const hasError = showError && !e.answer?.value;
        content = (
          <TextInput
            textArea={true}
            label={e.value}
            // placeholder={e.value}
            id={e.id}
            value={e.answer?.value ?? ''}
            onChange={handleChangeText(index)}
            hasError={hasError}
            subTitle={hasError ? fillInErrorMessage : ''}
            showValidationMessage
            allowSpecialCharacters={false}
          />
        );
        break
      }
      case FeedbackFormElementTypeEnum.radio:
      case FeedbackFormElementTypeEnum.radioAssessable: {
        const e = element as FeedbackFormElementSelect;
        const options = e.options.map(o => ({ label: o, value: o }));
        // A radio answer is always a single value; lodash widens the default to string | string[].
        const value = get(elements, `${index}.answer.value`, '') as string;
        const hasError = showError && !value;
        content = (
          <div>
            <div className={styles.label}>{element.value}</div>
            <RadioGroup
              value={value}
              options={options}
              onChange={handChangeRadio(index)}
              name={e.id}
              hasError={hasError}
              subTitle={hasError ? fillInErrorMessage : ''}
              touchable
            />
          </div>
        );
        break;
      }
      case FeedbackFormElementTypeEnum.checkbox:
      case FeedbackFormElementTypeEnum.checkboxAssessable: {
        const e = element as FeedbackFormElementSelect;
        const options = e.options.map(o => ({ label: o, value: o }));
        // A checkbox answer is always a list; lodash widens the default to string | string[].
        const value = get(elements, `${index}.answer.value`, []) as string[];
        const hasError = showError && value.length === 0;
        content = (
          <div>
            <div className={styles.label}>{element.value}</div>
            <CheckboxGroup
              value={value}
              options={options}
              onChange={handleChangeCheckbox(index)}
              hasError={hasError}
              subTitle={hasError ? fillInErrorMessage : ''}
              touchable
            />
          </div>
        );
        break;
      }
      case FeedbackFormElementTypeEnum.multiScale:
      case FeedbackFormElementTypeEnum.multiScaleAssessable: {
        const e = element as FeedbackFormElementMultiScale;
        const options = e.options ?? [];
        const statements = e.statements ?? [];
        const value = e.answer ?? {};
        const hasError = showError && (!value || Object.keys(value).length < statements.length);

        content = (
          <div>
            <div className={styles.label}>{element.value}</div>
            <MultiScaleComponent
              value={value}
              options={options}
              statements={statements}
              hasError={hasError}
              errorMessage={hasError ? 'Please select an answer for each question.' : ''}
              onChange={handleChangeMultiScaleAnswer(index)}
            />
          </div>
        );
        break;
      }
      default: {
        content = null;
      }
    }
    const firstRow = index === 0;
    return (
      <div className={`${styles.row} ${firstRow ? styles.firstRow : styles.QARowItem}`} key={element.id} style={{ minWidth: '80%' }}>
        {content}
        {element.askExplanation && (
          <TextInput
            label={() => <label className={styles.smallLabel} htmlFor={`${element.id}_askExplanation`}>{"Please explain your Answer"}</label>}
            placeholder="Please explain your Answer"
            id={`${element.id}_askExplanation`}
            value={element.answer?.answerExplanation ?? ''}
            onChange={handleChangeExplanation(index)}
            hasError={showError && (!element.answer?.answerExplanation || element.answer?.answerExplanation === '')}
            subTitle={showError && (!element.answer?.answerExplanation || element.answer?.answerExplanation === '') ? fillInErrorMessage : ''}
            className={styles.answerExplanation}
            small
            showValidationMessage
            allowSpecialCharacters={false}
          />
        )}
      </div>
    );
  }, [elements, handChangeRadio, handleChangeCheckbox, handleChangeExplanation, handleChangeMultiScaleAnswer, handleChangeText, showError]);

  const goToNextStep = () => {
    if (!elements[currentIndex + 1]) return
    const hasError = currentIndex < 0 ? false : feedbackSingleFormHasAnswerError(elements[currentIndex]);

    if (hasError) {
      setShowError(true);
      logger.debug(`[Exit Ticket] Current question ${currentIndex+1} is incomplete, cannot proceed`);
    } else {
      logger.debug(`[Exit Ticket] Moving from question ${currentIndex+1} to ${currentIndex+2}`);
      setIndex((index) => index + 1);
      setShowError(false); // Reset error state when moving to next question
    }
  }

  const goToPreviousStep = () => {
    setShowError(false);
    // Prevent going below 0
    setIndex((index) => Math.max(0, index - 1))
  }

  return (
    <>
      {elements[currentIndex] && renderQAItem(elements[currentIndex], currentIndex)}
      <div className={styles.oneRow} style={{ width: '100%' }}>
        { currentIndex >= 0 ? <Button disabled={loading} className={styles.simpleButton} label={'Previous'} onClick={goToPreviousStep} size={isMobile ? 'tiny' : 'small'}/> : <div></div>}
        { currentIndex === elements.length - 1 ? (
            <Button disabled={loading} className={styles.simpleButton} label={'Submit'} onClick={handleSubmit} size={isMobile ? 'tiny' : 'small'}/>
          ) : (
            <Button disabled={loading} className={styles.simpleButton} label={' Next '} onClick={goToNextStep} size={isMobile ? 'tiny' : 'small'}/>
          )
        }
      </div>
    </>
  );
}

export default memo(FeedbackForm);
